import { extendTheme } from "@chakra-ui/react";
import { colors, radius, shadows, typography } from "./tokens";

const config = {
  initialColorMode: "light",
  useSystemColorMode: false,
};

// Map our tokens into Chakra's semantic tokens so components automatically
// flip to dark mode when colorMode === "dark".
const semanticTokens = {
  colors: {
    "bg.canvas":     { default: colors.light.bg.canvas,   _dark: colors.dark.bg.canvas },
    "bg.surface":    { default: colors.light.bg.surface,  _dark: colors.dark.bg.surface },
    "bg.subtle":     { default: colors.light.bg.subtle,   _dark: colors.dark.bg.subtle },
    "bg.elevated":   { default: colors.light.bg.elevated, _dark: colors.dark.bg.elevated },
    "border.subtle": { default: colors.light.border.subtle,  _dark: colors.dark.border.subtle },
    "border.default":{ default: colors.light.border.default, _dark: colors.dark.border.default },
    "border.strong": { default: colors.light.border.strong,  _dark: colors.dark.border.strong },
    "text.primary":  { default: colors.light.text.primary,   _dark: colors.dark.text.primary },
    "text.secondary":{ default: colors.light.text.secondary, _dark: colors.dark.text.secondary },
    "text.tertiary": { default: colors.light.text.tertiary,  _dark: colors.dark.text.tertiary },
    "accent.primary":   { default: colors.light.accent.primary,      _dark: colors.dark.accent.primary },
    "accent.hover":     { default: colors.light.accent.primaryHover, _dark: colors.dark.accent.primaryHover },
    "accent.soft":      { default: colors.light.accent.primarySoft,  _dark: colors.dark.accent.primarySoft },
    "success":          { default: colors.light.semantic.success, _dark: colors.dark.semantic.success },
    "warning":          { default: colors.light.semantic.warning, _dark: colors.dark.semantic.warning },
    "danger":           { default: colors.light.semantic.danger,  _dark: colors.dark.semantic.danger },
    "info":             { default: colors.light.semantic.info,    _dark: colors.dark.semantic.info },
  },
  shadows: {
    "elev.xs":  { default: shadows.light.xs,  _dark: shadows.dark.xs },
    "elev.sm":  { default: shadows.light.sm,  _dark: shadows.dark.sm },
    "elev.md":  { default: shadows.light.md,  _dark: shadows.dark.md },
    "elev.lg":  { default: shadows.light.lg,  _dark: shadows.dark.lg },
    "elev.xl":  { default: shadows.light.xl,  _dark: shadows.dark.xl },
    "glow":     { default: shadows.light.glow, _dark: shadows.dark.glow },
    "ring":     { default: shadows.light.ring, _dark: shadows.dark.ring },
  },
};

const styles = {
  global: (props) => ({
    body: {
      bg: "bg.canvas",
      color: "text.primary",
      fontFamily: typography.fontFamily,
      fontFeatureSettings: '"cv11", "ss01", "ss03"',
      WebkitFontSmoothing: "antialiased",
      MozOsxFontSmoothing: "grayscale",
    },
    "*, *::before, *::after": {
      borderColor: "border.subtle",
    },
    // Tabular numerics for dashboard metrics
    ".tabular": { fontVariantNumeric: "tabular-nums" },
  }),
};

const components = {
  Button: {
    baseStyle: {
      fontWeight: 500,
      borderRadius: radius.md,
      transition: "all 160ms cubic-bezier(0.4, 0, 0.2, 1)",
      _focusVisible: { boxShadow: "ring" },
    },
    variants: {
      solid: (props) => ({
        bg: "accent.primary",
        color: "white",
        _hover: { bg: "accent.hover", transform: "translateY(-1px)", boxShadow: "elev.md", _disabled: { transform: "none" } },
        _active: { transform: "translateY(0)" },
      }),
      ghost: { _hover: { bg: "bg.subtle" } },
      outline: { borderColor: "border.default", _hover: { bg: "bg.subtle" } },
    },
  },
  Modal: {
    baseStyle: {
      dialog: { borderRadius: radius.xl, bg: "bg.elevated", boxShadow: "elev.xl" },
      overlay: { backdropFilter: "blur(8px)", bg: "blackAlpha.500" },
    },
  },
  Menu: {
    baseStyle: {
      list: { borderRadius: radius.lg, bg: "bg.elevated", borderColor: "border.subtle", boxShadow: "elev.lg", py: 1 },
      item: { _hover: { bg: "bg.subtle" }, _focus: { bg: "bg.subtle" } },
    },
  },
  Input: {
    variants: {
      outline: {
        field: {
          borderRadius: radius.md,
          borderColor: "border.default",
          bg: "bg.surface",
          _hover: { borderColor: "border.strong" },
          _focusVisible: { borderColor: "accent.primary", boxShadow: "ring" },
        },
      },
    },
  },
  Card: {
    baseStyle: {
      container: { bg: "bg.surface", borderRadius: radius.lg, boxShadow: "elev.sm", borderWidth: "1px", borderColor: "border.subtle" },
    },
  },
};

const fonts = {
  body: typography.fontFamily,
  heading: typography.fontFamily,
  mono: typography.fontMono,
};

const theme = extendTheme({
  config,
  fonts,
  semanticTokens,
  styles,
  components,
});

export default theme;
